import { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/useAuth';
import { Button } from '@/components/ui/button';
import { PlusCircle, Search, MoreHorizontal, Pencil, Trash2, Database, FileSpreadsheet, FileText, Server, Globe, RefreshCcw, LayoutGrid, List, Cloud, Smartphone, ArrowRight } from 'lucide-react';
// @ts-ignore
import { StandardPageLayout, Button as CommonButton, SortDropdown } from '../components/Common.jsx';
import { DataSourceWizard } from '@/components/datasources/DataSourceWizard';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { toast } from 'sonner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { apiGet, apiDelete } from '@/api';

interface DataSource {
    id: string;
    name: string;
    type: number;
    connectionString: string;
    workspaceId: string;
    gatewayId?: string | null;
    updated_at?: string;
    created_at?: string;
}

const getTypeIcon = (type: number) => {
    switch (type) {
        case 9: // CSV
            return <FileText className="h-5 w-5 text-emerald-500" />;
        case 10: // Excel
            return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
        case 0: // PostgreSQL
        case 1: // MySQL
        case 2: // SQL Server
            return <Database className="h-5 w-5 text-blue-500" />;
        case 4: // Snowflake
        case 5: // BigQuery
        case 6: // Redshift
            return <Server className="h-5 w-5 text-sky-400" />;
        case 3: // Oracle
        case 7: // Azure SQL
        case 13: // DuckDB
            return <Database className="h-5 w-5 text-red-500" />; // Oracle/DuckDB/AzureSQL
        case 15: // MCP
            return <Smartphone className="h-5 w-5 text-purple-500" />;
        case 17: // Salesforce
        case 16: // Azure Blob
        case 18: // AWS S3
            return <Cloud className="h-5 w-5 text-blue-400" />;
        default:
            return <Globe className="h-5 w-5 text-slate-400" />;
    }
};

const getTypeName = (type: number) => {
    const types: Record<number, string> = {
        0: 'PostgreSQL',
        1: 'MySQL',
        2: 'SQL Server',
        3: 'Oracle',
        4: 'Snowflake',
        5: 'BigQuery',
        6: 'Redshift',
        7: 'Azure SQL',
        9: 'CSV File',
        10: 'Excel File',
        13: 'DuckDB',
        15: 'MCP Server',
        16: 'Azure Blob Storage',
        17: 'Salesforce',
        18: 'AWS S3 Bucket'
    };
    return types[type] || 'Unknown';
};

export default function DataSourcesPage() {
    const [dataSources, setDataSources] = useState<DataSource[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deleteId, setDeleteId] = useState<string | null>(null); // In-app confirm
    const [searchQuery, setSearchQuery] = useState('');
    const [view, setView] = useState<'grid' | 'list'>('grid');
    const [sortOption, setSortOption] = useState<any>('updated_desc');
    const { token } = useAuth();

    const canEdit = true;

    useEffect(() => {
        fetchDataSources();
    }, [token]);

    const fetchDataSources = async () => {
        if (!token) return;
        try {
            const data = await apiGet('/datasources', token);
            setDataSources(data || []);
        } catch (error) {
            console.error('Failed to fetch data sources', error);
            toast.error("Failed to load data sources");
        } finally {
            setIsLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await apiDelete(`/datasources/${deleteId}`, token);
            toast.success("Data source deleted successfully");
            setDataSources(prev => prev.filter(ds => ds.id !== deleteId));
        } catch (error) {
            console.error('Failed to delete', error);
            toast.error("Failed to delete data source");
        } finally {
            setDeleteId(null);
        }
    };

    const handleEdit = (id: string) => {
        setEditingId(id);
        setIsCreating(true);
    };

    const handleCloseWizard = () => {
        setIsCreating(false);
        setEditingId(null);
    };

    const filteredSources = dataSources.filter(ds =>
        ds.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const sortedSources = [...filteredSources].sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
        const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
        if (sortOption === 'updated_desc') return dateB - dateA;
        if (sortOption === 'updated_asc') return dateA - dateB;
        if (sortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
        if (sortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
        return 0;
    });

    const headerActions = (
        <CommonButton onClick={() => setIsCreating(true)} sx={{ height: 30 }}>
            <PlusCircle className="mr-2 h-4 w-4" /> ADD SOURCE
        </CommonButton>
    );

    const filterControls = (
        <div className="flex items-center gap-4">
            <div className="relative max-w-md group w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-[#002F5D] dark:text-slate-500 transition-colors" />
                <input
                    type="text"
                    placeholder="Search connections..."
                    className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 py-2 pl-10 pr-4 rounded-xl shadow-sm text-sm focus:ring-2 focus:ring-blue-100 dark:text-slate-100 transition-shadow outline-none placeholder:text-gray-400 dark:placeholder:text-slate-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            <Button variant="ghost" size="icon" onClick={() => fetchDataSources()} className="text-slate-500 dark:text-slate-400 hover:text-[#002F5D] hover:bg-slate-100 dark:hover:bg-slate-800 h-8 w-8">
                <RefreshCcw className="h-4 w-4" />
            </Button>

            <div className="flex items-center bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800 p-1">
                <Button
                    variant={view === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={`px-2 h-8 ${view === 'grid' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setView('grid')}
                >
                    <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                    variant={view === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={`px-2 h-8 ${view === 'list' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setView('list')}
                >
                    <List className="w-4 h-4" />
                </Button>
            </div>
            <SortDropdown value={sortOption} onChange={setSortOption} />
        </div>
    );

    return (
        <StandardPageLayout
            title="Data Sources"
            description="Manage your data connections. Connect to databases, files, and cloud services to start analyzing your data."
            headerActions={headerActions}
            filterControls={filterControls}
        >
            {isLoading ? (
                <div className="flex-1 flex items-center justify-center h-64">
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#002F5D] border-t-transparent" />
                        Loading connections...
                    </div>
                </div>
            ) : sortedSources.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-12 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-3xl bg-gray-50/50 dark:bg-slate-900/50 mt-4">
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-full shadow-sm mb-4">
                        <Database className="h-8 w-8 text-gray-300 dark:text-slate-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">No connections found</h3>
                    <p className="text-gray-500 dark:text-slate-400 text-sm mt-2 max-w-xs">Create your first data connection to start analyzing.</p>
                    <Button
                        variant="ghost"
                        onClick={() => setIsCreating(true)}
                        className="mt-6 text-[#002F5D] hover:bg-blue-50 dark:hover:bg-slate-800"
                    > Create Connection <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </div>
            ) : view === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 content-start pb-10">
                    {sortedSources.map((ds) => (
                        <div
                            key={ds.id}
                            onClick={() => handleEdit(ds.id)}
                            className="group bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer relative overflow-hidden"
                        >
                            {/* Decorative grad */}
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#002F5D] to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center justify-center p-2.5 bg-blue-50 dark:bg-slate-800 rounded-xl group-hover:bg-[#002F5D] group-hover:text-white transition-colors duration-300">
                                    {getTypeIcon(ds.type)}
                                </div>
                                <div className="flex gap-1">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-40 bg-white dark:bg-slate-900 shadow-xl border-gray-100 dark:border-slate-800">
                                            <DropdownMenuItem
                                                className="hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer gap-2 my-1"
                                                onClick={() => handleEdit(ds.id)}
                                            >
                                                <Pencil className="h-4 w-4" /> Edit
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                className="text-red-600 dark:text-red-400 cursor-pointer focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20"
                                                onClick={(e) => { e.stopPropagation(); setDeleteId(ds.id); }}
                                            >
                                                <Trash2 className="h-4 w-4" /> Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h3 className="font-bold text-gray-900 dark:text-slate-100 mb-1 group-hover:text-[#002F5D] dark:group-hover:text-blue-400 transition-colors truncate" title={ds.name}>{ds.name}</h3>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                    {getTypeName(ds.type)}
                                </div>
                            </div>

                            <div className="pt-4 border-t border-gray-50 dark:border-slate-800 flex items-center justify-between text-xs text-slate-400 dark:text-slate-500">
                                <span className="flex items-center gap-1.5 font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full">
                                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                    Active
                                </span>
                                <span className="font-mono text-[10px] text-slate-300 dark:text-slate-600">ID: {ds.id.slice(0, 6)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
                    <Table>
                        <TableHeader className="bg-gray-50/50 dark:bg-slate-900/50">
                            <TableRow className="border-b border-gray-200 dark:border-slate-800">
                                <TableHead className="w-[60px]"></TableHead>
                                <TableHead className="text-gray-500 dark:text-slate-400 font-semibold h-12">Name</TableHead>
                                <TableHead className="text-gray-500 dark:text-slate-400 font-semibold h-12">Type</TableHead>
                                <TableHead className="text-right text-gray-500 dark:text-slate-400 font-semibold h-12 pr-6">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedSources.map((ds) => (
                                <TableRow key={ds.id} className="cursor-pointer hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-gray-100 dark:border-slate-800" onClick={() => handleEdit(ds.id)}>
                                    <TableCell className="text-center pl-4">
                                        <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                            {getTypeIcon(ds.type)}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col py-1">
                                            <span className="font-semibold text-gray-900 dark:text-slate-100 text-[15px]">{ds.name}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                            {getTypeName(ds.type)}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right pr-4">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" className="h-8 w-8 p-0 text-slate-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                                    <span className="sr-only">Open menu</span>
                                                    <MoreHorizontal className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 shadow-lg">
                                                <DropdownMenuItem
                                                    className="hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer gap-2 my-1"
                                                    onClick={(e) => { e.stopPropagation(); handleEdit(ds.id); }}
                                                >
                                                    <Pencil className="h-4 w-4" /> Edit Configuration
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="hover:bg-red-50 dark:hover:bg-red-950/20 text-red-600 hover:text-red-700 dark:hover:text-red-500 cursor-pointer gap-2 my-1"
                                                    onClick={(e) => { e.stopPropagation(); setDeleteId(ds.id); }}
                                                >
                                                    <Trash2 className="h-4 w-4" /> Delete Connection
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Wizard Modal */}
            <AnimatePresence>
                {isCreating && (
                    <DataSourceWizard
                        isOpen={isCreating}
                        onClose={handleCloseWizard}
                        dataSourceId={editingId}
                        onSuccess={() => {
                            handleCloseWizard();
                            fetchDataSources();
                            toast.success(editingId ? "Connection updated successfully" : "Connection created successfully");
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={!!deleteId}
                title="Delete Connection"
                message="Are you sure you want to delete this connection? This action cannot be undone."
                confirmText="Delete"
                isDestructive={true}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteId(null)}
            />
        </StandardPageLayout>
    );
}
